"use client";

import { useEffect, useRef, useState } from "react";
import { site } from "@/lib/site";
import { WhatsappIcon } from "@/components/ui/social-icons";

/**
 * The round WhatsApp chat button. It floats at the bottom-right of the viewport while browsing, and once the
 * footer's "Get in touch" slot scrolls into view it docks into that slot instead.
 */
export function FooterWhatsApp() {
  const slotRef = useRef<HTMLDivElement>(null);
  const [docked, setDocked] = useState(false);
  const [top, setTop] = useState(0);

  useEffect(() => {
    const el = slotRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setDocked(entry.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // While docked the button stays on the "Get in touch" line but sits at the right edge of the screen.
  useEffect(() => {
    if (!docked) return;
    const place = () => slotRef.current && setTop(slotRef.current.getBoundingClientRect().top);
    place();
    window.addEventListener("scroll", place, { passive: true });
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place);
      window.removeEventListener("resize", place);
    };
  }, [docked]);

  const href = `${site.socials.whatsapp}?text=${encodeURIComponent(`Hi ${site.name}, I'd like to know more about your courses.`)}`;
  return (
    <div ref={slotRef} className="h-11 w-11 shrink-0">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
        title="Chat with us on WhatsApp"
        style={docked ? { top } : undefined}
        className={`group fixed z-50 flex items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_10px_30px_-8px_rgba(37,211,102,0.7)] transition-transform duration-300 hover:scale-110 ${
          docked ? "right-4 h-11 w-11 sm:right-6" : "bottom-5 right-5 h-14 w-14 sm:bottom-6 sm:right-6"
        }`}
      >
        <span className="absolute inset-0 animate-ping rounded-full bg-[#25D366] opacity-30 motion-reduce:hidden" aria-hidden />
        <WhatsappIcon className={`relative ${docked ? "h-6 w-6" : "h-7 w-7"}`} />
      </a>
    </div>
  );
}
